// src/components/admin/AdminDashboard.jsx
// Admin 外壳：Tab 注册 + 项目过滤 + 布局。
// 所有 tab 已拆分为独立组件（./tabs/* 与 ./CostAnalyticsTab.jsx），
// 共享组件在 ./adminUi.js，共享工具在 ./adminUtils.js。
import React, { useCallback, useEffect, useState } from "react";
import {
  Activity,
  AlertTriangle,
  Coins,
  Clapperboard,
  Database,
  FlaskConical,
  FileClock,
  FileText,
  FolderKanban,
  Images,
  KeyRound,
  LayoutDashboard,
  ReceiptText,
  Server,
  ServerCog,
  TrendingUp,
  Trash2,
  Users as UsersIcon,
} from "lucide-react";
import { fetchProjects } from "../../services/adminService";
import { ErrorNotice, ProjectCombobox } from "./adminUi.jsx";
import OverviewTab from "./tabs/OverviewTab";
import ProjectsTab from "./tabs/ProjectsTab";
import RecycleBinTab from './tabs/RecycleBinTab';
import CostAnalyticsTab from "./CostAnalyticsTab";
import MiniStoryboardStatisticsTab from './tabs/MiniStoryboardStatisticsTab';
import PointsCenterTab from "./tabs/PointsCenterTab";
import VisualReferenceAdminTab from "./tabs/VisualReferenceAdminTab";
import PromptCatalogTab from "./tabs/PromptCatalogTab";
import RuntimeSecretsTab from "./tabs/RuntimeSecretsTab";
import HongguoDataTab from "./tabs/HongguoDataTab";
import HongguoRankingsTab from "./tabs/HongguoRankingsTab";
import ErrorsTab from "./tabs/ErrorsTab";
import AiTasksTab from "./tabs/AiTasksTab";
import LiveTasksTab from "./tabs/LiveTasksTab";
import UserStatsTab from "./tabs/UserStatsTab";
import ActivityLogTab from "./tabs/ActivityLogTab";
import HealthTab from "./tabs/HealthTab";

const TABS = [
  { key: 'overview', label: '概览', icon: LayoutDashboard, Comp: OverviewTab },
  { key: 'projects', label: '项目', icon: FolderKanban, Comp: ProjectsTab },
  { key: 'recycle-bin', label: '回收站', icon: Trash2, Comp: RecycleBinTab },
  { key: 'costs', label: '费用', icon: ReceiptText, Comp: CostAnalyticsTab },
  { key: 'mini-storyboard-stats', label: '小故事板统计', icon: Clapperboard, Comp: MiniStoryboardStatisticsTab },
  { key: 'points', label: '积分中心', icon: Coins, Comp: PointsCenterTab },
  { key: 'visualRefs', label: '视觉参考', icon: Images, Comp: VisualReferenceAdminTab },
  { key: 'prompts', label: '提示词库', icon: FileText, Comp: PromptCatalogTab },
  { key: 'secrets', label: '密钥配置', icon: KeyRound, Comp: RuntimeSecretsTab },
  { key: 'hongguo', label: '红果数据测试', icon: FlaskConical, Comp: HongguoDataTab },
  { key: 'hongguo-rankings', label: '红果榜单', icon: TrendingUp, Comp: HongguoRankingsTab },
  { key: 'errors', label: '错误', icon: AlertTriangle, Comp: ErrorsTab },
  { key: 'ai-tasks', label: 'AI任务', icon: Database, Comp: AiTasksTab },
  { key: 'live', label: '实时任务', icon: Activity, Comp: LiveTasksTab },
  { key: 'users', label: '用户统计', icon: UsersIcon, Comp: UserStatsTab },
  { key: 'activity', label: '操作日志', icon: FileClock, Comp: ActivityLogTab },
  { key: 'health', label: '系统健康', icon: Server, Comp: HealthTab },
];

const AdminDashboard = ({ initialTab }) => {
  const [active, setActive] = useState(() => {
    const queryTab = new URLSearchParams(window.location.search).get('tab');
    const requested = initialTab || queryTab || 'overview';
    return TABS.some(t => t.key === requested) ? requested : 'overview';
  });
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectLoadError, setProjectLoadError] = useState('');
  const [projectSearching, setProjectSearching] = useState(false);

  const ActiveComp = TABS.find(t => t.key === active)?.Comp || TABS[0].Comp;

  const mergeProjectOptions = useCallback((rows) => {
    setProjectOptions(current => {
      const merged = new Map();
      [...(rows || []), ...current].forEach(project => {
        if (project?.id != null) merged.set(String(project.id), project);
      });
      return Array.from(merged.values()).slice(0, 500);
    });
  }, []);

  const loadProjectOptions = useCallback(async (keyword = '') => {
    setProjectLoadError('');
    setProjectSearching(true);
    try {
      const rows = await fetchProjects({ keyword, limit: 100 });
      mergeProjectOptions(rows);
    } catch (e) {
      console.error(e);
      setProjectLoadError(e?.message || '项目过滤列表加载失败');
    } finally {
      setProjectSearching(false);
    }
  }, [mergeProjectOptions]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchProjects({ limit: 100 });
        if (!cancelled) setProjectOptions(rows || []);
      } catch (e) {
        console.error(e);
        if (!cancelled) setProjectLoadError(e?.message || '项目过滤列表加载失败');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // initialTab 变化时在渲染期同步到激活 tab（render-adjust 模式，避免 effect 级联渲染）
  const [prevInitialTab, setPrevInitialTab] = useState(initialTab);
  if (prevInitialTab !== initialTab) {
    setPrevInitialTab(initialTab);
    const requested = initialTab || new URLSearchParams(window.location.search).get('tab');
    if (requested && TABS.some(tab => tab.key === requested)) setActive(requested);
  }

  const selectTab = useCallback((key) => {
    setActive(key);
    const params = new URLSearchParams(window.location.search);
    params.set('tab', key);
    const query = params.toString();
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
  }, []);

  // 已登录：展示 Tab
  return (
    <div className="w-full h-screen p-4 md:p-6 xl:p-8 overflow-y-auto font-sans"
      style={{ backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-primary)' }}>
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-5 border-b pb-4" style={{ borderColor: 'var(--color-border-primary)' }}>
          <div>
            <div className="flex items-baseline gap-3">
              <h1 className="text-xl font-semibold">管理后台</h1>
              <span className="text-label">ADMIN</span>
            </div>
            <div className="text-[13px] mt-1.5" style={{ color: 'var(--color-text-muted)' }}>
              用户、错误、AI 任务、费用、积分和项目维度查询
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <a
              href="/operations"
              className="h-8 px-3 rounded-md text-xs font-medium border inline-flex items-center gap-1.5 shrink-0 transition-colors duration-150"
              style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-accent-primary)', backgroundColor: 'var(--color-bg-tertiary)' }}
            >
              <TrendingUp size={14} />
              运营工作台
            </a>
            <a
              href="/admin/provider-probe"
              className="h-8 px-3 rounded-md text-xs font-medium border inline-flex items-center gap-1.5 shrink-0 transition-colors duration-150"
              style={{
                borderColor: 'var(--color-border-primary)',
                color: 'var(--color-text-secondary)',
                backgroundColor: 'var(--color-bg-tertiary)',
              }}
              aria-label="打开代理能力测试"
            >
              <ServerCog size={14} />
              代理测试
            </a>
            <label className="text-label">项目过滤</label>
            <ProjectCombobox
              value={selectedProjectId}
              onChange={setSelectedProjectId}
              projectOptions={projectOptions}
              maxWidth={320}
              onSearch={loadProjectOptions}
              searching={projectSearching}
            />
          </div>
        </div>
        {projectLoadError && <ErrorNotice message={projectLoadError} />}

        <div className="flex gap-1 mb-5 border-b overflow-x-auto" style={{ borderColor: 'var(--color-border-primary)' }}>
          {TABS.map(t => {
            const Icon = t.icon;
            const isActive = active === t.key;
            return (
              <button key={t.key} onClick={() => selectTab(t.key)}
                className="px-3.5 py-2 text-[13px] flex items-center gap-1.5 transition-colors duration-150 shrink-0 cursor-pointer"
                style={{
                  borderBottom: isActive ? '2px solid var(--color-warning)' : '2px solid transparent',
                  color: isActive ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
                  fontWeight: isActive ? 600 : 400,
                }}>
                <Icon size={13} style={isActive ? { color: 'var(--color-warning)' } : undefined} />
                {t.label}
              </button>
            );
          })}
        </div>

        <div key={active} className="animate-in fade-in duration-150">
          <ActiveComp
            projectId={selectedProjectId}
            projectOptions={projectOptions}
            onProjectSelect={setSelectedProjectId}
            selectedProjectId={selectedProjectId}
            onProjectsChanged={loadProjectOptions}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
